import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

// Sun=0, Mon=1, ..., Sat=6
const WEEKDAY_LIST = [
  { num: 0, ar: 'الأحد',     en: 'Sun' },
  { num: 1, ar: 'الإثنين',   en: 'Mon' },
  { num: 2, ar: 'الثلاثاء',  en: 'Tue' },
  { num: 3, ar: 'الأربعاء',  en: 'Wed' },
  { num: 4, ar: 'الخميس',    en: 'Thu' },
  { num: 5, ar: 'الجمعة',    en: 'Fri' },
  { num: 6, ar: 'السبت',     en: 'Sat' },
];

const VSMHolidays = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const T = {
    title:           { ar: '🌴 الإجازات وأيام العطلة', en: '🌴 Holidays & Off Days' },
    subtitle:        { ar: 'إعدادات أيام عدم العمل', en: 'Non-working days settings' },
    weeklyOff:       { ar: 'العطلة الأسبوعية', en: 'Weekly Off Days' },
    weeklyOffHint:   { ar: 'اختر أيام الأسبوع التي لا يتم فيها تنفيذ الخدمات', en: 'Days when no services run' },
    saveWeekly:      { ar: 'حفظ', en: 'Save' },
    weeklySaved:     { ar: '✓ تم الحفظ', en: '✓ Saved' },
    holidays:        { ar: 'الإجازات الرسمية والأعياد', en: 'Public Holidays & Closures' },
    addHoliday:      { ar: '+ إضافة إجازة', en: '+ Add Holiday' },
    edit:            { ar: '✏️ تعديل الإجازة', en: '✏️ Edit Holiday' },
    create:          { ar: '➕ إجازة جديدة', en: '➕ New Holiday' },
    holidayDate:     { ar: 'التاريخ', en: 'Date' },
    nameEn:          { ar: 'الاسم (إنجليزي)', en: 'Name (English)' },
    nameAr:          { ar: 'الاسم (عربي)', en: 'Name (Arabic)' },
    type:            { ar: 'النوع', en: 'Type' },
    typePublic:      { ar: 'إجازة رسمية', en: 'Public Holiday' },
    typeCompany:     { ar: 'إجازة شركة', en: 'Company Closure' },
    isRecurring:     { ar: 'متكررة سنوياً', en: 'Recurring Yearly' },
    recurringHint:   { ar: '(تنطبق كل سنة في نفس اليوم)', en: '(Applies every year on same day)' },
    notes:           { ar: 'ملاحظات', en: 'Notes' },
    save:            { ar: '💾 حفظ', en: '💾 Save' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    cancel:          { ar: 'إلغاء', en: 'Cancel' },
    actions:         { ar: 'الإجراءات', en: 'Actions' },
    loading:         { ar: 'جاري التحميل...', en: 'Loading...' },
    noHolidays:      { ar: 'لا توجد إجازات. اضغط "إضافة إجازة" للبدء.', en: 'No holidays. Click "Add Holiday".' },
    confirmDelete:   { ar: 'هل أنت متأكد من حذف هذه الإجازة؟', en: 'Delete this holiday?' },
    saveError:       { ar: 'فشل الحفظ', en: 'Failed to save' },
    deleteError:     { ar: 'فشل الحذف', en: 'Failed to delete' },
    duplicate:       { ar: 'يوجد إجازة بهذا التاريخ بالفعل', en: 'Holiday on this date already exists' },
    nameRequired:    { ar: 'الاسم مطلوب (عربي وإنجليزي)', en: 'Name required (AR + EN)' },
    yearFilter:      { ar: 'السنة', en: 'Year' },
    allYears:        { ar: 'كل السنوات', en: 'All Years' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  // ===== Weekly Off Days State =====
  const [weeklyOff, setWeeklyOff] = useState([5]); // Friday default
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [weeklySavedMsg, setWeeklySavedMsg] = useState(false);

  // ===== Holidays State =====
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());

  const emptyForm = {
    holiday_date: '',
    name: '',
    name_ar: '',
    type: 'public_holiday',
    is_recurring: false,
    notes: ''
  };
  const [form, setForm] = useState({ ...emptyForm });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ===== Fetch weekly off days =====
  const fetchWeeklyOff = useCallback(async () => {
    setWeeklyLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/settings/weekly-off-days`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setWeeklyOff(data.data.weekly_off_days || []);
    } catch (err) {
      console.error('[VSM] Fetch weekly off error:', err);
    }
    setWeeklyLoading(false);
  }, []);

  useEffect(() => { fetchWeeklyOff(); }, [fetchWeeklyOff]);

  const toggleWeeklyDay = (dayNum) => {
    setWeeklyOff(prev => prev.includes(dayNum)
      ? prev.filter(d => d !== dayNum)
      : [...prev, dayNum].sort()
    );
  };

  const saveWeeklyOff = async () => {
    setWeeklySaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/settings/weekly-off-days`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ weekly_off_days: weeklyOff })
      });
      const data = await res.json();
      if (data.success) {
        setWeeklySavedMsg(true);
        setTimeout(() => setWeeklySavedMsg(false), 2500);
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      alert(tr('saveError'));
    }
    setWeeklySaving(false);
  };

  // ===== Fetch holidays =====
  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (yearFilter) params.append('year', yearFilter);
    try {
      const res = await fetch(`${API}/holidays?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setHolidays(data.data);
    } catch (err) {
      console.error('[VSM] Fetch holidays error:', err);
    }
    setLoading(false);
  }, [yearFilter]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // ===== Form actions =====
  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); };
  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (h, e) => {
    e?.stopPropagation();
    setForm({
      holiday_date: h.holiday_date ? h.holiday_date.slice(0, 10) : '',
      name: h.name || '',
      name_ar: h.name_ar || '',
      type: h.type || 'public_holiday',
      is_recurring: !!h.is_recurring,
      notes: h.notes || ''
    });
    setEditId(h.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.holiday_date) return;
    if (!form.name?.trim() || !form.name_ar?.trim()) {
      alert(tr('nameRequired')); return;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const url = editId ? `${API}/holidays/${editId}` : `${API}/holidays`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        resetForm();
        fetchHolidays();
      } else {
        if (res.status === 409) alert(tr('duplicate'));
        else alert(data.error || tr('saveError'));
      }
    } catch (err) {
      alert(tr('saveError'));
    }
    setSaving(false);
  };

  const handleDelete = async (h, e) => {
    e?.stopPropagation();
    if (!window.confirm(`${tr('confirmDelete')}\n\n${isRTL ? h.name_ar : h.name}`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/holidays/${h.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchHolidays();
      else alert(data.error || tr('deleteError'));
    } catch (err) {
      alert(tr('deleteError'));
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
  };

  // Year options for filter
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // ===== Styles =====
  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '11px', color: '#94a3b8', fontWeight: '600',
    display: 'block', marginBottom: '4px'
  };
  const sectionStyle = {
    background: 'rgba(15,23,42,0.5)', borderRadius: '12px',
    padding: '20px', marginBottom: '16px',
    border: '1px solid rgba(255,255,255,0.08)'
  };
  const sectionTitle = (icon, text) => (
    <h3 style={{
      fontSize: '15px', fontWeight: '700', color: '#f1f5f9',
      margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px',
      paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)'
    }}>{icon} {text}</h3>
  );
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const modalBox = {
    background: '#1e293b', borderRadius: '16px', padding: '24px',
    maxHeight: '90vh', overflow: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
    direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
  };
  const tableHeaderStyle = {
    padding: '10px 12px', fontSize: '12px', color: '#94a3b8',
    fontWeight: '600', textAlign: isRTL ? 'right' : 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  };
  const tableCellStyle = {
    padding: '12px', fontSize: '13px', color: '#e2e8f0',
    borderBottom: '1px solid rgba(255,255,255,0.04)'
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1100px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
            {tr('title')}
          </h1>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
            {tr('subtitle')}
          </p>
        </div>

        {/* SECTION 1: Weekly Off Days */}
        <div style={sectionStyle}>
          {sectionTitle('📅', tr('weeklyOff'))}
          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>
            {tr('weeklyOffHint')}
          </p>

          {weeklyLoading ? (
            <div style={{ color: '#94a3b8', fontSize: '13px' }}>{tr('loading')}</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {WEEKDAY_LIST.map(day => {
                  const active = weeklyOff.includes(day.num);
                  return (
                    <button
                      key={day.num}
                      type="button"
                      onClick={() => toggleWeeklyDay(day.num)}
                      style={{
                        padding: '10px 18px', borderRadius: '10px',
                        border: active ? '2px solid #ef4444' : '2px solid rgba(255,255,255,0.15)',
                        background: active ? 'rgba(239,68,68,0.2)' : 'rgba(15,23,42,0.5)',
                        color: active ? '#fca5a5' : '#cbd5e1',
                        fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                        minWidth: '85px',
                        transition: 'all 0.15s'
                      }}
                    >
                      {active && '🚫 '}{isRTL ? day.ar : day.en}
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={saveWeeklyOff}
                  disabled={weeklySaving}
                  style={{
                    background: weeklySaving ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    color: '#fff', border: 'none',
                    padding: '10px 24px', borderRadius: '8px',
                    fontSize: '13px', cursor: weeklySaving ? 'not-allowed' : 'pointer',
                    fontWeight: '700'
                  }}
                >
                  {weeklySaving ? tr('saving') : tr('saveWeekly')}
                </button>
                {weeklySavedMsg && (
                  <span style={{ color: '#6ee7b7', fontSize: '13px', fontWeight: '600' }}>
                    {tr('weeklySaved')}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* SECTION 2: Holidays */}
        <div style={sectionStyle}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '14px', flexWrap: 'wrap', gap: '10px',
            paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9', margin: 0,
              display: 'flex', alignItems: 'center', gap: '8px' }}>
              🎉 {tr('holidays')}
            </h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value ? parseInt(e.target.value, 10) : '')}
                style={{ ...inputStyle, maxWidth: '120px', cursor: 'pointer', padding: '8px 10px' }}
              >
                <option value="">{tr('allYears')}</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button
                onClick={openCreate}
                style={{
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
                  border: 'none', padding: '8px 16px', borderRadius: '8px',
                  fontWeight: '600', fontSize: '13px', cursor: 'pointer'
                }}
              >{tr('addHoliday')}</button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>{tr('loading')}</div>
          ) : holidays.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>{tr('noHolidays')}</div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <th style={tableHeaderStyle}>{tr('holidayDate')}</th>
                    <th style={tableHeaderStyle}>{tr('nameAr')}</th>
                    <th style={tableHeaderStyle}>{tr('type')}</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('isRecurring')}</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map(h => (
                    <tr key={h.id}>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: '700', color: '#fcd34d' }}>
                          {formatDate(h.holiday_date)}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: '600' }}>
                          {isRTL ? h.name_ar : h.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {isRTL ? h.name : h.name_ar}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{
                          background: h.type === 'public_holiday' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                          color: h.type === 'public_holiday' ? '#6ee7b7' : '#fcd34d',
                          padding: '3px 10px', borderRadius: '12px',
                          fontSize: '11px', fontWeight: '600'
                        }}>
                          {h.type === 'public_holiday' ? tr('typePublic') : tr('typeCompany')}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        {h.is_recurring ? (
                          <span style={{ color: '#a5b4fc', fontSize: '16px' }}>🔄</span>
                        ) : (
                          <span style={{ color: '#475569' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={(e) => openEdit(h, e)}
                            style={{
                              background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                              border: '1px solid rgba(99,102,241,0.3)',
                              padding: '5px 10px', borderRadius: '6px',
                              fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                            }}
                          >✏️</button>
                          <button
                            onClick={(e) => handleDelete(h, e)}
                            style={{
                              background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                              border: '1px solid rgba(239,68,68,0.25)',
                              padding: '5px 10px', borderRadius: '6px',
                              fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                            }}
                          >🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div style={modalOverlay} onClick={() => setShowForm(false)}>
            <div style={{ ...modalBox, width: '550px', maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 16px' }}>
                {editId ? tr('edit') : tr('create')}
              </h2>
              <form onSubmit={handleSubmit}>

                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{tr('holidayDate')} *</label>
                  <input
                    type="date"
                    value={form.holiday_date}
                    onChange={e => handleChange('holiday_date', e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>{tr('nameEn')} *</label>
                    <input
                      value={form.name}
                      onChange={e => handleChange('name', e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>{tr('nameAr')} *</label>
                    <input
                      value={form.name_ar}
                      onChange={e => handleChange('name_ar', e.target.value)}
                      style={{ ...inputStyle, textAlign: 'right' }}
                      dir="rtl"
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{tr('type')}</label>
                  <select
                    value={form.type}
                    onChange={e => handleChange('type', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="public_holiday">{tr('typePublic')}</option>
                    <option value="company_closure">{tr('typeCompany')}</option>
                  </select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={form.is_recurring}
                      onChange={e => handleChange('is_recurring', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    🔄 {tr('isRecurring')}
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '400' }}>
                      {tr('recurringHint')}
                    </span>
                  </label>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>{tr('notes')}</label>
                  <textarea
                    value={form.notes}
                    onChange={e => handleChange('notes', e.target.value)}
                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                    rows={2}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    style={{
                      background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
                      border: '1px solid rgba(100,116,139,0.3)',
                      padding: '10px 20px', borderRadius: '8px',
                      fontSize: '13px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >{tr('cancel')}</button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      background: saving ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      color: '#fff', border: 'none',
                      padding: '10px 24px', borderRadius: '8px',
                      fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: '700'
                    }}
                  >{saving ? tr('saving') : tr('save')}</button>
                </div>

              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VSMHolidays;
